<template>
  <main class="container-1">

    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <TabNav :links="routesNav" />
    </div>

    <div class="container-body">

      <section class="monitoring-section">
        <div class="monitoring-section__header">
          <h3>Auditoría de Terreno</h3>
        </div>

        <div class="monitoring-filters monitoring-filters--dates">
          <label class="monitoring-filter-label">Desde</label>
          <input v-model="dateFrom" type="date" class="monitoring-filter-date" @change="applyFilter" />
          <label class="monitoring-filter-label">Hasta</label>
          <input v-model="dateTo" type="date" class="monitoring-filter-date" @change="applyFilter" />
          <button v-if="dateFrom || dateTo" class="monitoring-filter-clear" @click="clearDateFilter" title="Limpiar fechas">✕</button>
        </div>
        <div class="monitoring-filters">
          <select v-model="filterAction" @change="applyFilter" class="monitoring-select">
            <option value="">Todas las acciones</option>
            <option v-for="option in actionOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
          <select v-model="filterResourceType" @change="applyFilter" class="monitoring-select">
            <option value="">Todos los recursos</option>
            <option v-for="option in resourceTypeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
          <select v-model="filterResult" @change="applyFilter" class="monitoring-select">
            <option value="">Todos los resultados</option>
            <option value="success">Éxito</option>
            <option value="failure">Fallo</option>
          </select>
          <button v-if="hasActiveFilters" type="button" class="filters-clear-btn" @click="clearAllFilters">Limpiar filtros</button>
        </div>

        <p v-if="loading" class="monitoring-hint">Cargando eventos de auditoría…</p>
        <p v-else-if="errorMessage" class="monitoring-error" role="alert">{{ errorMessage }}</p>
        <template v-else>
          <table v-if="items.length > 0" class="monitoring-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Actor</th>
                <th>Acción</th>
                <th>Recurso</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in items" :key="event.id">
                <td>{{ event.occurredAt ? formatDate(event.occurredAt) : 'Entrada Vacía' }}</td>
                <td>{{ (event.actor && (event.actor.usernameSnapshot || event.actor.userId)) || 'Entrada Vacía' }}</td>
                <td>{{ actionLabel(event.action) }}</td>
                <td>
                  <span :class="'monitoring-module monitoring-module--' + event.resourceType">
                    {{ resourceTypeLabel(event.resourceType) }}
                  </span>
                  {{ event.resourceId }}
                </td>
                <td>
                  <span :class="'monitoring-op monitoring-op--' + event.result">
                    {{ resultLabel(event.result) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="monitoring-empty">{{ hasActiveFilters ? 'Sin resultados para el filtro aplicado.' : 'No hay eventos de auditoría disponibles.' }}</p>
        </template>

        <div v-show="totalPages > 1" class="pagination-wrap">
          <Pagination
            :totalPages="totalPages"
            :currentPage="currentPage"
            :pageSize="pageSize"
            @pagechanged="pageChangedClick"
          />
        </div>
      </section>

    </div>

  </main>
</template>

<script>
var moment = require('moment')

import Breadcrumb from '@/components/Breadcrumb.vue'
import Pagination from '@/components/Pagination.vue'
import TabNav from '@/components/TabNav.vue'
import { pagination } from '@/components/mixin/pagination.js'
import { createTerrainApi } from '@/services/terrain/api.js'

// Vocabulario cerrado de acciones/tipos de recurso que emite el backend
// (services/terrain/*.go vía audit.Record) — no hay endpoint que los liste
// dinámicamente, así que se mantienen a mano igual que filterOpType en
// MonitoringGeneral.vue.
const ACTION_LABELS = {
  'campaign.published': 'Campaña publicada',
  'campaign.closed': 'Campaña cerrada',
  'campaign.soft_deleted': 'Campaña eliminada',
  'campaign.hard_deleted': 'Campaña eliminada definitivamente',
  'campaign.recipients.expanded': 'Destinatarios ampliados',
  'campaign.recipient.withdrawn': 'Destinatario retirado',
  'template.published': 'Plantilla publicada',
  'response.submitted': 'Respuesta enviada',
  'response.review_started': 'Revisión iniciada',
  'response.observed': 'Respuesta observada',
  'response.approved': 'Respuesta aprobada',
  'response.rejected': 'Respuesta rechazada',
  'response.corrected': 'Respuesta corregida',
  'response.corrected_by_recipient': 'Respuesta corregida por destinatario',
  'response.withdrawn': 'Respuesta retirada',
  'response.transition': 'Cambio de estado',
  'attachment.downloaded': 'Adjunto descargado',
  'attachment.download_denied': 'Descarga de adjunto denegada',
  'attachment.rejected': 'Adjunto rechazado',
  'prefillCase.admin_released': 'Caso prellenado liberado por administrador',
}

const RESOURCE_TYPE_LABELS = {
  campaign: 'Campaña',
  response: 'Respuesta',
  templateVersion: 'Versión de plantilla',
  campaignRecipient: 'Destinatario de campaña',
  attachment: 'Adjunto',
  prefillCase: 'Caso prellenado',
}

export default {
  name: 'MonitoringTerrain',
  components: { Breadcrumb, Pagination, TabNav },
  mixins: [pagination],
  data() {
    return {
      items: [],
      loading: true,
      errorMessage: '',
      pageSize: 25,
      dateFrom: '',
      dateTo: '',
      filterAction: '',
      filterResourceType: '',
      filterResult: '',
    }
  },
  computed: {
    routesNav() {
      return [
        { name: 'Secciones' },
        { name: 'General', path: '/monitoreo/general' },
        { name: 'Mapas', path: '/monitoreo/mapas' },
        { name: 'Capas', path: '/monitoreo/capas' },
        { name: 'Terreno', path: '/monitoreo/terreno' },
      ]
    },
    routesBread() {
      return [{ name: 'Monitoreo' }]
    },
    terrainApi() {
      return createTerrainApi(this.$http, this.$store.getters.backendurl)
    },
    actionOptions() {
      return Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label }))
    },
    resourceTypeOptions() {
      return Object.entries(RESOURCE_TYPE_LABELS).map(([value, label]) => ({ value, label }))
    },
    hasActiveFilters() {
      return this.dateFrom !== '' || this.dateTo !== '' || this.filterAction !== '' ||
        this.filterResourceType !== '' || this.filterResult !== ''
    },
  },
  methods: {
    formatDate(date) {
      moment.locale('es')
      return moment(date).format('DD/MM/YYYY HH:mm:ss')
    },
    actionLabel(action) { return ACTION_LABELS[action] || action },
    resourceTypeLabel(type) { return RESOURCE_TYPE_LABELS[type] || type },
    resultLabel(result) { return ({ success: 'Éxito', failure: 'Fallo' })[result] || 'No disponible' },
    buildParams(page) {
      const params = { page, size: this.pageSize }
      if (this.dateFrom) params.from = new Date(this.dateFrom + 'T00:00:00').toISOString()
      if (this.dateTo) params.to = new Date(this.dateTo + 'T23:59:59').toISOString()
      if (this.filterAction) params.action = this.filterAction
      if (this.filterResourceType) params.resourceType = this.filterResourceType
      if (this.filterResult) params.result = this.filterResult
      return params
    },
    async loadAuditEvents(page) {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await this.terrainApi.listAuditEvents(this.buildParams(page))
        this.items = (response.data && response.data.items) || []
        this.totalPages = (response.data && response.data.totalPages) || 0
        this.currentPage = page
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: 'inicio', query: { returnTo: this.$route.fullPath } })
          return
        }
        this.errorMessage = (error.response && error.response.status === 403)
          ? 'No tiene autorización para consultar la auditoría de terreno.'
          : 'No fue posible cargar la auditoría. Intente nuevamente.'
      } finally {
        this.loading = false
      }
    },
    applyFilter() { this.loadAuditEvents(1) },
    pageChangedClick(page) { this.loadAuditEvents(page) },
    clearDateFilter() {
      this.dateFrom = ''
      this.dateTo = ''
      this.applyFilter()
    },
    clearAllFilters() {
      this.dateFrom = ''
      this.dateTo = ''
      this.filterAction = ''
      this.filterResourceType = ''
      this.filterResult = ''
      this.applyFilter()
    },
  },
  created() {
    const permissions = this.$store.getters.getPermissions || {}
    const terrain = permissions.terrain || {}
    if (!permissions.monitoring && !terrain.manage && !terrain.review) {
      this.$router.push('/pagina-no-encontrada')
      return
    }
    this.loadAuditEvents(1)
  },
}
</script>

<style lang="scss" scoped>
.monitoring-section {
  margin-bottom: 1rem;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;

    h3 { margin: 0; }
  }
}

.monitoring-hint { color: #888; font-size: 0.8em; }
.monitoring-empty { color: #888; font-style: italic; }

.monitoring-error {
  color: var(--color-danger);
  font-size: 0.85em;
}

.monitoring-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;

  &--dates { flex-wrap: nowrap; }
}

.monitoring-filter-label {
  font-size: 0.82em;
  color: #666;
  white-space: nowrap;
}

.monitoring-filter-date {
  padding: 0.3rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85em;
  outline: none;
  min-width: 0;

  &:focus { border-color: var(--color-steel); }
}

.monitoring-filter-clear {
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #888;
  cursor: pointer;
  font-size: 0.8em;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;

  &:hover { color: #c0392b; border-color: #c0392b; }
}

.monitoring-select {
  padding: 0.25rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
}

.monitoring-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;

  th, td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e5e5e5;
  }

  th {
    background: var(--color-navy);
    color: #fff;
    font-weight: 600;
  }

  tr:hover td { background: #fafafa; }
}

.monitoring-op {
  display: inline-block;
  padding: 0.15em 0.5em;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 600;

  &--success { background: #d4edda; color: #155724; }
  &--failure { background: #f8d7da; color: #721c24; }
}

// Un color por tipo de recurso, tomados de la misma paleta que
// .monitoring-module en MonitoringGeneral.vue (documentos/visor/capas/
// usuarios/perfil) para no introducir un tercer set de colores.
.monitoring-module {
  display: inline-block;
  padding: 0.15em 0.5em;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 600;
  white-space: nowrap;
  margin-right: 0.35em;

  &--attachment         { background: #fef9c3; color: #854d0e; }
  &--campaign           { background: #dbeafe; color: #1e40af; }
  &--response           { background: #dcfce7; color: #166534; }
  &--templateVersion    { background: #ede9fe; color: #5b21b6; }
  &--campaignRecipient  { background: #cffafe; color: #155e75; }
  &--prefillCase        { background: #fce7f3; color: #9d174d; }
}

.pagination-wrap { margin-top: 1rem; }
</style>
